import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {
    BsCalendar2Week,
    BsChevronDown,
    BsEnvelope,
    BsGenderAmbiguous,
    BsGeoAltFill,
    BsPerson,
    BsPersonVcard,
    BsTelephone
} from 'react-icons/bs'
import {MdChurch, MdLocationOn} from 'react-icons/md'
import {PiBookOpenText, PiCross} from 'react-icons/pi'
import {IdentifierTypes} from 'views/AppLocals/appFieldTypes'
import _ from 'lodash'
import {formatSimplifyDate} from "../../../../helpers/dateUtils";
import {ItemInformation} from 'components/common/ItemInformation'
import {Avatar} from "antd";
import {FaUserGraduate} from "react-icons/fa6";

const InfoRow = ({icon, label, value, primary = false}) => {
    if (!value) return null
    return (
        <div className="flex items-center gap-2">
            <span className="p-2 flex flex-center text-color-300 background-200 rounded-lg">
                {icon}
            </span>
            <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs text-color-400 tracking-wider leading-snug">
                    {label}
                </span>
                <span className="font-semibold text-xs text-gray-800 leading-snug break-words tracking-wider">
                    {value}
                </span>
            </div>
        </div>
    )
}

const SectionDivider = ({label}) => (
    <div className="flex flex-col gap-0.5">
        <span className="font-semibold text-xs text-gray-500 uppercase tracking-widest whitespace-nowrap">
            {label}
        </span>
        <div style={{ height: 3 }} className="w-20 bg-gray-400 rounded-full"/>
    </div>
)

const GenderLabels = {0: 'unknown', 1: 'male', 2: 'female', 3: 'other'}

export const EnrollmentInfoPanel = ({enrollment}) => {
    const t = useTranslate()
    const [expanded, setExpanded] = useState(false)

    const {
        fullname,
        patron_saint_name,
        date_of_birth,
        gender,
        identifier_type,
        identifier_code,
        contact_name,
        contact_phone,
        contact_email,
        contact_address,
        parish_name,
        diocese_name,
        religious_order_name,
        registered_subjects = [],
        notes,
    } = enrollment ?? {}

    const identifierLabel = _.find(IdentifierTypes, {value: identifier_type})?.label

    const yearOfBirth = date_of_birth
        ? new Date(date_of_birth).getFullYear()
        : null

    const age = yearOfBirth ? new Date().getFullYear() - yearOfBirth : null

    return (
        <div className="p-3 flex flex-col gap-4 background-50 rounded-xl">

            {/* Header: name + patron saint */}
            <div className="pb-4 border-b border-color-100 flex flex-col">
                <span className="font-semibold text-xs text-secondary-600 tracking-widest whitespace-nowrap">
                    {t('student')}
                </span>
                <div style={{ height: 3 }} className="mt-0.5 mb-2 w-20 bg-secondary-400 rounded-full"/>
                <div className="flex items-center gap-3">
                    <Avatar
                        size={40}
                        icon={<FaUserGraduate size={20} className="text-secondary-500"/>}
                        className="flex flex-center bg-secondary-50"
                    />
                    <div className="flex flex-col gap-1">
                        <div className="flex items-baseline gap-1.5">
                            {patron_saint_name && (
                                <span className="font-medium text-color-000">
                                    {patron_saint_name}
                                </span>
                            )}
                            <span className="font-bold text-lg text-color-000 leading-snug">
                            {fullname}
                        </span>
                        </div>
                        {religious_order_name && (
                            <ItemInformation
                                showIcon={true}
                                label="religious orders" value={religious_order_name}
                                className="text-xs tracking-wider italic"
                            />
                        )}
                    </div>
                </div>
            </div>
            {/* Personal info */}
            <div className="flex flex-col gap-2">
                <SectionDivider label={t('personal information')}/>
                <div className="ml-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {yearOfBirth && (
                        <InfoRow
                            icon={<BsCalendar2Week size={14}/>}
                            label={t('date of birth')}
                            value={age ? `${formatSimplifyDate(date_of_birth, '', 'DD/MM/YYYY')} (${age} ${t('years old')})` : String(yearOfBirth)}
                        />
                    )}
                    {gender !== undefined && gender !== null && (
                        <InfoRow
                            icon={<BsGenderAmbiguous size={14}/>}
                            label={t('gender')}
                            value={t(GenderLabels[gender] ?? 'unknown')}
                        />
                    )}
                    {patron_saint_name && (
                        <InfoRow
                            icon={<PiCross size={14}/>}
                            label={t('patron saint')}
                            value={patron_saint_name}
                        />
                    )}
                    {identifier_code && (
                        <InfoRow
                            icon={<BsPersonVcard size={14}/>}
                            label={t(identifierLabel ?? 'identifier')}
                            value={identifier_code}
                        />
                    )}
                </div>
            </div>
            {/* Subjects */}
            {registered_subjects.length > 0 && (
                <div className="flex flex-col gap-2">
                    <SectionDivider label={t('subjects registered')}/>
                    <div className="flex items-center gap-x-4 gap-y-1">
                        {registered_subjects.map((s, i) => (
                            <span
                                key={i}
                                className="w-fit px-2 py-0.5 flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full font-medium text-xs text-blue-600">
                                <PiBookOpenText size={13}/>
                                {s?.title || s?.name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <span
                onClick={() => setExpanded(v => !v)}
                className="flex items-center justify-end gap-1 text-secondary hover:text-secondary-600 cursor-pointer">
                    <span className="font-medium text-xs tracking-wide">
                        {expanded ? t('see less') : t('see more')}
                    </span>
                    <BsChevronDown
                        size={12}
                        style={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 0.3s',
                        }}
                    />
            </span>
            {expanded && (
                <>
                    {/* Contact */}
                    <div className="flex flex-col gap-2">
                        <SectionDivider label={t('contact')}/>
                        <div className="ml-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {contact_name && (
                                <InfoRow
                                    primary={true}
                                    icon={<BsPerson size={14}/>}
                                    label={t('contact name')}
                                    value={contact_name}
                                />
                            )}
                            {contact_phone && (
                                <InfoRow
                                    icon={<BsTelephone size={14}/>}
                                    label={t('phone')}
                                    value={contact_phone}
                                />
                            )}
                            {contact_email && (
                                <InfoRow
                                    icon={<BsEnvelope size={14}/>}
                                    label={t('email')}
                                    value={contact_email}
                                />
                            )}
                            {contact_address && (
                                <InfoRow
                                    icon={<BsGeoAltFill size={14}/>}
                                    label={t('address')}
                                    value={contact_address}
                                />
                            )}
                        </div>
                    </div>

                    {/* Location */}
                    {(parish_name || diocese_name) && (
                        <div className="flex flex-col gap-2">
                            <SectionDivider label={t('parish')}/>
                            <div className="ml-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                                {parish_name && (
                                    <InfoRow
                                        icon={<MdChurch size={14}/>}
                                        label={t('belong to parish')}
                                        value={parish_name}
                                    />
                                )}
                                {diocese_name && (
                                    <InfoRow
                                        icon={<MdLocationOn size={14}/>}
                                        label={t('diocese')}
                                        value={diocese_name}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {/* Notes */}
                    {notes && (
                        <div className="flex flex-col gap-2">
                            <SectionDivider label={t('notes')}/>
                            <p className="text-xs text-color-300 italic tracking-wider">
                                {notes}
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
